import { Component, computed, HostListener, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule, DOCUMENT } from '@angular/common';

import { ICellEditorParams } from 'ag-grid-enterprise';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { QuillModule } from 'ngx-quill';
import { QUILL_MODULES_TEXTAREA } from '../quill-editor.utils';
import { delay, noop, take, tap, timer } from 'rxjs';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, QuillModule],
  template: `
    <div class="quill-editor-container">
      <quill-editor class="w-full" [(ngModel)]="value" [modules]="quillModules" />
      <span class="block mt-2 text-xs text-gray-400 text-right">{{ shortcutHint() }}</span>
    </div>
  `,
  styles: [
    `
      .quill-editor-container {
        background: white;
        padding: 8px;
        min-width: 400px;
        max-width: 600px;
      }
    `,
  ],
})
export class TsAgQuillEditor implements ICellEditorAngularComp {
  protected value: string = '';
  protected params: ICellEditorParams = null;
  protected readonly quillModules = QUILL_MODULES_TEXTAREA;

  private readonly docRef = inject(DOCUMENT);
  private originalValue: string = '';
  private isCancelled: boolean = false;

  private readonly isMac = computed(() => {
    const platform = this.docRef.defaultView?.navigator?.platform ?? '';
    return platform.toLowerCase().includes('mac');
  });

  protected readonly shortcutHint = computed(() =>
    this.isMac() ? '⌘ + Enter to submit' : 'Ctrl + Enter to submit',
  );

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.isCancelled = true;
      this.params.stopEditing(true);
      return;
    }
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      this.params.stopEditing();
      return;
    }

    event.stopPropagation();
  }

  @HostListener('click', ['$event'])
  onCLick(event: MouseEvent): void {
    event.stopPropagation();
  }

  agInit(params: ICellEditorParams): void {
    this.params = params;
    this.originalValue = params.value || '';
    this.value = this.originalValue;
    this.isCancelled = false;

    timer(100)
      .pipe(
        tap(() => (this.docRef.querySelector('.ag-popup-editor') as HTMLInputElement)?.focus()),
        delay(200),
        tap(() => (this.docRef.querySelector('.ql-editor') as HTMLInputElement)?.focus()),
        take(1),
      )
      .subscribe(noop);
  }

  isPopup?(): boolean {
    return true;
  }

  getPopupPosition?(): 'over' | 'under' | undefined {
    return 'under';
  }

  getValue(): any {
    return this.isCancelled ? this.originalValue : this.value;
  }
}
